import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
 
import { TaskCommentApi,TaskCommentDTO } from './TaskCommentApi';

export type CommentFormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: CommentFormMode;
  taskId: string;
  parentId?: string;
}

export const TaskCommentsForm: React.FC<Props> = ({ mode, taskId, parentId = '' }) => {
  const navigate = useNavigate();
  const { id } = useParams(); // comment id for edit/view
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user?.tenantId || '';
  const tenantName = user?.tenantName || '';
  const userId = user?.id || '';
  const username = user?.username || '';

  const [comment, setComment] = useState<TaskCommentDTO>({
    id: '',
    taskId,
    userId: userId,
    tenantId: tenantId,
    employeeId: '',
    employeeName: '',
    userName: username,
    content: '',
    parentId,
    isInternal: false,
    visibility: 'public',
    edited: false,
    editedAt: '',
    editedBy: '',
    deletedBy: '',
    deletedAt: '',
    isDeleted: false,
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
    children: [],
  });

  const [error, setError] = useState('');
  const isReadOnly = mode === 'view';

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && id) {
      loadCommentById(id);
    }
  }, [id, mode]);

  const loadCommentById = async (commentId: string) => {
    try {
      const response = await TaskCommentApi.getById(commentId);
      setComment(response.data);
    } catch {
      setError('Failed to load comment');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
  
    const checked = (e.target as HTMLInputElement).checked;
  
    setComment((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (mode === 'edit' && comment.id) {
        await TaskCommentApi.update(comment.id, comment);
        alert('Comment updated successfully!');
      } else if (mode === 'create') {
        await TaskCommentApi.create(comment);
        alert('Comment created successfully!');
      }
      navigate(-1);
    } catch {
      alert('Failed to save comment');
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto bg-white shadow rounded">
      {error && <div className="text-red-600 mb-4">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block font-semibold mb-1">Content</label>
          <textarea
            name="content"
            value={comment.content}
            onChange={handleChange}
            readOnly={isReadOnly}
            className={`w-full border rounded px-3 py-2 ${isReadOnly ? 'bg-gray-100' : ''}`}
            rows={4}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">Visibility</label>
            <select
              name="visibility"
              value={comment.visibility}
              onChange={handleChange}
              disabled={isReadOnly}
              className={`w-full border rounded px-3 py-2 ${isReadOnly ? 'bg-gray-100' : ''}`}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="isInternal"
              checked={comment.isInternal}
              onChange={handleChange}
              disabled={isReadOnly}
            />
            <label className="text-sm">Internal Comment</label>
          </div>
        </div>

        {mode !== 'view' && (
          <div className="flex gap-4">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {mode === 'edit' ? 'Update' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        )}
      </form>
    </div>
  );
};